import React, { useContext, useMemo } from 'react';

import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { alpha, useTheme } from '@mui/material/styles';

import { AuthContext } from 'src/auth/context';
import TransactionListDesktop from 'src/features/transactions/TransactionListDesktop';
import TransactionListMobile from 'src/features/transactions/TransactionListMobile';
import { useResponsive } from 'src/hooks/useResponsive';

const mockTransactions = [
  {
    _id: '1',
    amountIn: '2.5',
    amountOut: '5247.80',
    tokenIn: 'weth',
    tokenOut: 'usdt',
    createdAt: '2025-01-15T16:33:43.278Z',
    status: 'sent',
    from: '0xf39Fd6e51aad88F6F4ce6aB8827279cffFb92266',
    to: '0xDc64a140Aa3E981100a9becA4E685f962f0cF6C9',
    txHashOut: '0x19b6ca4a3e38356b3ec3358062c594aeb538f833ab41cd37bd2fd9400ca02bf3',
  },
  {
    _id: '2',
    amountIn: '1.0',
    amountOut: '2100.00',
    tokenIn: 'weth',
    tokenOut: 'usdc',
    createdAt: '2025-01-15T14:22:15.123Z',
    status: 'processing',
    from: '0xf39Fd6e51aad88F6F4ce6aB8827279cffFb92266',
    to: '0x70997970C51812dc3A010C7d01b50e0d17dc79C8',
    txHashOut: '0x2bc6ca4a3e38356b3ec3358062c594aeb538f833ab41cd37bd2fd9400ca02bf4',
  },
  {
    _id: '3',
    amountIn: '0.5',
    amountOut: '1250.00',
    tokenIn: 'weth',
    tokenOut: 'usdc',
    createdAt: '2025-01-15T12:15:30.456Z',
    status: 'failed',
    from: '0xf39Fd6e51aad88F6F4ce6aB8827279cffFb92266',
    to: '0x3C44CdDdB6a900fa2b585dd299e03d12FA4293BC',
  },
  {
    _id: '4',
    amountIn: '3.2',
    amountOut: '6720.00',
    tokenIn: 'weth',
    tokenOut: 'dai',
    createdAt: '2025-01-14T18:45:12.789Z',
    status: 'sent',
    from: '0xf39Fd6e51aad88F6F4ce6aB8827279cffFb92266',
    to: '0x90F79bf6EB2c4f870365E785982E1f101E93b906',
    txHashOut: '0x3dc6ca4a3e38356b3ec3358062c594aeb538f833ab41cd37bd2fd9400ca02bf5',
  },
  {
    _id: '5',
    amountIn: '0.8',
    amountOut: '1250.00',
    tokenIn: 'weth',
    tokenOut: 'dai',
    createdAt: '2025-01-14T09:30:45.012Z',
    status: 'pending',
    from: '0xf39Fd6e51aad88F6F4ce6aB8827279cffFb92266',
    to: '0x15d34AAf54267DB7D7c367839AAf71A00a2C6A65',
  },
];

const STATUSES = [
  { label: 'Successful', status: 'sent' },
  { label: 'Processing', status: 'processing' },
  { label: 'Pending', status: 'pending' },
  { label: 'Failed', status: 'failed' },
];

function TransactionsProtectedView() {
  const theme = useTheme();
  const { isUserAuthenticated } = useContext(AuthContext);
  const isMobile = useResponsive('down', 'sm');

  const statusStats = useMemo(
    () =>
      STATUSES.map((stat) => ({
        ...stat,
        count: mockTransactions.filter((tx) => tx.status === stat.status).length,
      })),
    []
  );

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        minHeight: { xs: 'calc(100vh - 220px)', sm: 480 },
      }}
    >
      <Box
        sx={{
          filter: !isUserAuthenticated ? 'blur(6px)' : 'none',
          pointerEvents: !isUserAuthenticated ? 'none' : 'auto',
          opacity: !isUserAuthenticated ? 0.55 : 1,
          transition: 'filter 0.3s, opacity 0.3s',
        }}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: 'repeat(2, minmax(0, 1fr))',
              md: 'repeat(4, minmax(0, 1fr))',
            },
            gap: 1.5,
            mb: 3,
          }}
        >
          {statusStats.map((stat) => (
            <Paper
              key={stat.label}
              elevation={0}
              sx={{
                borderRadius: 3,
                p: 2,
                border: `1px solid ${theme.palette.divider}`,
                bgcolor: 'background.paper',
              }}
            >
              <Typography variant="caption" color="text.secondary">
                {stat.label}
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 800, mt: 0.5 }}>
                {stat.count}
              </Typography>
            </Paper>
          ))}
        </Box>

        {isMobile ? (
          <TransactionListMobile transactions={mockTransactions} />
        ) : (
          <TransactionListDesktop transactions={mockTransactions} />
        )}
      </Box>

      {!isUserAuthenticated && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 10,
            px: 2,
            display: 'flex',
            justifyContent: 'center',
            alignItems: { xs: 'flex-start', sm: 'center' },
            pt: { xs: 4, sm: 0 },
          }}
        >
          <Paper
            elevation={0}
            sx={{
              width: '100%',
              maxWidth: 360,
              mx: 'auto',
              p: 3,
              borderRadius: 4,
              border: `1px solid ${theme.palette.divider}`,
              bgcolor: alpha(theme.palette.background.paper, 0.96),
              textAlign: 'center',
              boxShadow:
                theme.palette.mode === 'light'
                  ? '0 24px 48px rgba(16, 24, 40, 0.12)'
                  : '0 24px 48px rgba(0, 0, 0, 0.34)',
            }}
          >
            <Stack spacing={1.5} alignItems="center">
              <Typography variant="h6" sx={{ fontWeight: 800, letterSpacing: '-0.02em' }}>
                Sign in to view your history
              </Typography>

              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                Your swap activity, confirmations, and transaction details will appear here after
                login.
              </Typography>
            </Stack>
          </Paper>
        </Box>
      )}
    </Box>
  );
}

export default TransactionsProtectedView;
